import { useEffect } from "react";
import { siteConfig } from "@/lib/site-config";

interface SeoProps {
  title: string;
  description: string;
  path: string;
  ogType?: "website" | "article";
  ogImage?: string;
  jsonLd?: Record<string, unknown> | Record<string, unknown>[];
  noindex?: boolean;
}

/**
 * Imperative document-head manager.
 *
 * react-helmet-async v3 does not render tags in this Lovable/Vite build
 * (every route shipped the static index.html <title> and no canonical),
 * so SEO metadata is applied directly to <head> from an effect instead.
 * Existing tags from index.html are updated in place; page-specific
 * JSON-LD is tagged and cleaned up on route change to avoid leaking.
 */

const LD_FLAG = "data-seo-jsonld";

function upsertMeta(attr: "name" | "property", key: string, content: string) {
  let el = document.head.querySelector<HTMLMetaElement>(`meta[${attr}="${key}"]`);
  if (!el) {
    el = document.createElement("meta");
    el.setAttribute(attr, key);
    document.head.appendChild(el);
  }
  el.setAttribute("content", content);
}

function upsertLink(rel: string, href: string) {
  let el = document.head.querySelector<HTMLLinkElement>(`link[rel="${rel}"]`);
  if (!el) {
    el = document.createElement("link");
    el.setAttribute("rel", rel);
    document.head.appendChild(el);
  }
  el.setAttribute("href", href);
}

const Seo = ({ title, description, path, ogType = "website", ogImage, jsonLd, noindex }: SeoProps) => {
  const url = `${siteConfig.url}${path}`;
  const image = ogImage
    ? ogImage.startsWith("http")
      ? ogImage
      : `${siteConfig.url}${ogImage}`
    : `${siteConfig.url}${siteConfig.ogImage}`;

  useEffect(() => {
    document.title = title;

    upsertMeta("name", "description", description);
    upsertLink("canonical", url);

    // Only emit a robots directive when a page opts out of indexing (e.g. 404);
    // remove any stale one left by a previous route so normal pages stay indexable.
    document.head.querySelector('meta[name="robots"][data-seo]')?.remove();
    if (noindex) {
      const m = document.createElement("meta");
      m.setAttribute("name", "robots");
      m.setAttribute("content", "noindex, follow");
      m.setAttribute("data-seo", "");
      document.head.appendChild(m);
    }

    upsertMeta("property", "og:type", ogType);
    upsertMeta("property", "og:title", title);
    upsertMeta("property", "og:description", description);
    upsertMeta("property", "og:url", url);
    upsertMeta("property", "og:image", image);

    upsertMeta("name", "twitter:title", title);
    upsertMeta("name", "twitter:description", description);
    upsertMeta("name", "twitter:image", image);

    // Page-specific structured data — replace any from the previous route.
    document.head.querySelectorAll(`script[${LD_FLAG}]`).forEach((n) => n.remove());
    const ldArray = jsonLd ? (Array.isArray(jsonLd) ? jsonLd : [jsonLd]) : [];
    ldArray.forEach((ld) => {
      const s = document.createElement("script");
      s.type = "application/ld+json";
      s.setAttribute(LD_FLAG, "");
      s.text = JSON.stringify(ld);
      document.head.appendChild(s);
    });

    return () => {
      document.head.querySelectorAll(`script[${LD_FLAG}]`).forEach((n) => n.remove());
    };
  }, [title, description, url, image, ogType, jsonLd, noindex]);

  return null;
};

export default Seo;
